import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, RadialBarChart, RadialBar } from 'recharts';
import { Activity } from 'lucide-react';

const HealthScoreSection = ({ startupId }) => {
  const [healthData, setHealthData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (startupId) loadData();
  }, [startupId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [healthRes, chartRes] = await Promise.all([
        axios.get(`${API}/startups/${startupId}/health-score`, { withCredentials: true }),
        axios.get(`${API}/startups/${startupId}/metrics-chart`, { withCredentials: true }),
      ]);
      setHealthData(healthRes.data);
      setChartData(chartRes.data);
    } catch {
      // No benchmarks set yet - that's OK
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-800/40 border-slate-700 p-6" data-testid="health-score-loading">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-cyan-400"></div>
        </div>
      </Card>
    );
  }

  if (!healthData || healthData.message) {
    return (
      <Card className="bg-slate-800/40 border-slate-700 p-6" data-testid="health-score-no-benchmarks">
        <div className="flex items-center gap-3 mb-2">
          <Activity className="w-5 h-5 text-slate-500" />
          <h3 className="text-lg font-semibold text-slate-300">Health Score</h3>
        </div>
        <p className="text-slate-500 text-sm">No benchmarks have been set by admin yet. Health scores will appear once benchmarks are configured.</p>
      </Card>
    );
  }

  const { health_score, max_score, percentage, demerits, metric_scores } = healthData;

  const scoreColor = percentage >= 70 ? '#22c55e' : percentage >= 40 ? '#eab308' : '#ef4444';

  const gaugeData = [
    { name: 'Score', value: percentage, fill: scoreColor },
  ];

  // Build bar chart data
  const barData = chartData
    .filter(d => d.value !== null && d.value !== undefined)
    .map(d => ({
      name: d.metric_name.replace('Monthly Recurring ', 'MRR\n').replace('Customer Acquisition ', 'CAC\n').replace('Customer Growth ', 'Growth\n').replace('Product-Market Fit ', 'PMF\n').replace('Lifetime ', 'LTV\n').replace('Cash ', '').replace(' Rate', ''),
      value: d.value,
      benchmark: d.benchmark,
    }));

  return (
    <div className="space-y-6" data-testid="health-score-section">
      {/* Health Score Gauge */}
      <Card className="bg-slate-800/40 border-slate-700 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Activity className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-slate-100">Startup Health Score</h3>
        </div>
        <div className="flex items-center gap-8">
          <div className="w-48 h-48 relative" data-testid="health-gauge">
            <RadialBarChart
              width={192}
              height={192}
              cx={96}
              cy={96}
              innerRadius={60}
              outerRadius={85}
              barSize={16}
              data={gaugeData}
              startAngle={90}
              endAngle={-270}
            >
              <RadialBar
                background={{ fill: 'rgba(51,65,85,0.5)' }}
                dataKey="value"
                cornerRadius={8}
              />
            </RadialBarChart>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold" style={{ color: scoreColor }}>{percentage}%</span>
              <span className="text-xs text-slate-500">{health_score}/{max_score}</span>
            </div>
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-2 gap-3">
              {metric_scores && metric_scores.map((ms, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-700/30 rounded-lg px-3 py-2">
                  <span className="text-sm text-slate-300 truncate mr-2">{ms.metric_name.replace('Monthly Recurring Revenue', 'MRR').replace('Customer Acquisition Cost', 'CAC').replace('Customer Growth Rate', 'Growth').replace('Product-Market Fit Score', 'PMF').replace('Lifetime Value', 'LTV').replace('Cash Runway', 'Runway').replace('Burn Rate', 'Burn').replace('Churn Rate', 'Churn')}</span>
                  <span className={`text-sm font-medium ${ms.score === ms.max_score ? 'text-green-400' : ms.score > 0 ? 'text-amber-400' : 'text-red-400'}`}>
                    {ms.score}/{ms.max_score}
                  </span>
                </div>
              ))}
            </div>
            {demerits > 0 && (
              <p className="text-xs text-red-400 mt-3">-{demerits} points deducted for inactivity</p>
            )}
          </div>
        </div>
      </Card>

      {/* Metrics vs Benchmarks Chart */}
      {barData.length > 0 && (
        <Card className="bg-slate-800/40 border-slate-700 p-6" data-testid="metrics-chart">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Your Metrics vs Benchmarks</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={barData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(51,65,85,0.5)" />
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} interval={0} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#e2e8f0' }}
                itemStyle={{ color: '#94a3b8' }}
              />
              <Bar dataKey="value" name="Your Value" radius={[4, 4, 0, 0]}>
                {barData.map((entry, index) => (
                  <Cell
                    key={index}
                    fill={entry.benchmark && entry.value >= entry.benchmark ? '#22c55e' : '#06b6d4'}
                  />
                ))}
              </Bar>
              <Bar dataKey="benchmark" name="Benchmark" fill="#475569" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-6 mt-3 justify-center">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-cyan-500"></div>
              <span className="text-xs text-slate-400">Your Value</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-slate-600"></div>
              <span className="text-xs text-slate-400">Benchmark</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-green-500"></div>
              <span className="text-xs text-slate-400">Exceeds Benchmark</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default HealthScoreSection;
